import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import logo from '../img/logo-softmining.svg';
import Header from '../components/header';
import { useAuth } from '../context/AuthContext';
import { Link } from 'react-router-dom';
import { processPdbId, processPdbIdList } from '../Utility/CleanPDB/cleanPDBUtils';
import { retrieveMineData, consumeToken } from '../Utility/ConsumeToken/consumeToken';
import { FaArrowRight } from 'react-icons/fa';
import { AiOutlineInfoCircle } from 'react-icons/ai';
import { navigateToNextPage } from "../Utility/CreatePipeline/createPipelineUtils";
import DownloadPdbStorageLoader from '../components/PDBStorageLoader';


const CleanPdbComponent = () => {
  const { user } = useAuth();
  const isAdmin = user?.data?.admin === true;

  const [pdbId, setPdbId] = useState('');
  const [multiPdbInput, setMultiPdbInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [mode, setMode] = useState('single'); // 'single' | 'file'
  const [showModal, setShowModal] = useState(false);

  // Checkboxes REALI (ora si inviano al backend)
  const [uiOptions, setUiOptions] = useState({
    keepWater: false,        // -> kw
    preserveNucleic: false,  // -> pdna
    preserveCofactors: false,// -> coff
    oligodimerize: false,    // -> olig
  });

  const location = useLocation();
  const navigate = useNavigate();

  // --- parse & validate .txt (come prima) ---
  const parseAndValidateTxt = (text) => {
    const linesRaw = text.split(/\r?\n/);
    const lines = linesRaw.map(l => l.trim()).filter(Boolean);
    if (lines.length === 0) return { ok: false, error: 'The file is empty.' };
    if (lines.length > 10) return { ok: false, error: 'The file must contain at most 10 lines.' };

    const ids = [];
    for (let i = 0; i < lines.length; i++) {
      const tokens = lines[i].split(/\s+/).filter(Boolean);
      if (tokens.length !== 1) return { ok: false, error: `Invalid format at line ${i + 1}: exactly one PDB ID per line is required.` };
      const id = tokens[0].toUpperCase();
      if (!/^[A-Z0-9]{4}$/.test(id)) return { ok: false, error: `Invalid PDB ID at line ${i + 1}: "${tokens[0]}". Expected 4 alphanumeric characters.` };
      ids.push(id);
    }
    return { ok: true, ids };
  };

  const handleSearch = async () => {
    setLoading(true);
    setResults([]);

    // Mappa UI -> payload backend
    const flags = {
      kw:   uiOptions.keepWater        ? "1" : "0",
      pdna: uiOptions.preserveNucleic  ? "1" : "0",
      coff: uiOptions.preserveCofactors? "1" : "0",
      olig: uiOptions.oligodimerize    ? "1" : "0",
    };

    try {
      const apitoken = await retrieveMineData();
      if (apitoken.tokens > 1) {
        await consumeToken(-2);
        let newResults = [];
        if (mode === 'single') {
          const result = await processPdbId(apitoken, pdbId.trim().toUpperCase(), flags);
          newResults = [result];
        } else {
          // modalità multipla: prendo i PDB IDs dalla textarea
          if (!multiPdbInput.trim()) {
            throw new Error('Please insert at least one PDB ID.');
          }

          const res = parseAndValidateTxt(multiPdbInput);
          if (!res.ok) {
            throw new Error(res.error);
          }

          // res.ids è l'array di PDB IDs validati (max 10 linee)
          newResults = await processPdbIdList(res.ids, apitoken, flags);
        }
        setResults(newResults);
        setShowModal(true);
      } else {
        alert('Insufficient token');
      }
    } catch (error) {
      const msg = error?.response?.data?.error || error?.message || 'Unexpected error';
      alert(msg);
    } finally {
      setLoading(false);
    }
  };

  const proceedPipeline = () => {
    if (!location.state) return;
    const { params = {}, board = [], nextPageIndex = 0 } = location.state;
    const newParams = { ...params, pdbResults: results };
    navigateToNextPage(nextPageIndex, newParams, board, navigate);
  };

  const isSubmitDisabled = loading || (mode === 'single' && !pdbId.trim()) || (mode === 'file' && !multiPdbInput.trim());

  return (
    <>
      <Header />
        <div className="riga headerTools">
          <h3 className="titolo titoloFeature">CleanPDB</h3>        
          <span className="description text">
            PDB files from <a href="https://www.rcsb.org" target="_blank" rel="noreferrer">www.rcsb.org</a> are not immediately ready for computational analysis. CleanPDB automates crucial preprocessing steps such as checking hydrogen atoms, completing missing residues, adjusting pKa values, setting correct protonation states, handling water molecules, and managing cofactors. With CleanPDB, your protein structures are optimized and ready for molecular simulations, docking studies, and other computational workflows.
          </span>
          <div className="space"></div>
        </div>     
        <center>
        <div className="form formLogin formTools">
          <img src={logo} className="sign-logo" alt="logo" />

          <label className="selectLabel">Choose how many PDB IDs to send:</label>
          <div className="mode-selection">
            <button type="button" className={`mode-button ${mode === 'single' ? 'active' : ''}`} onClick={() => setMode('single')}>One PDB ID</button>
            <button type="button" className={`mode-button ${mode === 'file' ? 'active' : ''}`} onClick={() => setMode('file')}>Multiple PDB IDs</button>
          </div>

          <form onSubmit={(e) => { e.preventDefault(); handleSearch(); }}>
            {mode === 'single' && (
              <>
                <div>
                  <label htmlFor="pdbId">PDB ID:</label>
                  <input
                    placeholder="Enter PDB ID..."
                    type="text"
                    id="pdbId"
                    value={pdbId}
                    onChange={(e) => setPdbId(e.target.value)}
                    required
                    style={{ textTransform: 'uppercase' }}
                  />
                </div>
              </>
            )}

            {mode === 'file' && (
              <>
                <div>
                  <label htmlFor="multiPdbInput">
                    Enter PDB IDs (one per line - max 10 lines):
                  </label>
                  <textarea
                    id="multiPdbInput"
                    rows={3}
                    placeholder={`e.g.\n1ABC\n2XYZ`}
                    value={multiPdbInput}
                    onChange={(e) => setMultiPdbInput(e.target.value)}
                    required
                    style={{ resize: 'vertical', textTransform: 'uppercase' }}
                  />
                </div>
              </>
            )}

            {/* CHECKBOXES */}
            <div style={{ display: 'flex', alignItems: 'center', width: '100%', marginTop: 5 }}>
              <input
                type="checkbox"
                id="keepWater"
                checked={uiOptions.keepWater}
                onChange={(e) => setUiOptions(o => ({ ...o, keepWater: e.target.checked }))}
              />
              <label htmlFor="keepWater" style={{ marginLeft: 8 }}>
                Keep water molecules <AiOutlineInfoCircle style={{ cursor: 'help' }} className="textlink" title="Remove water molecules present in the PDB file from NMR or X-ray structures." />
              </label>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
              <input
                type="checkbox"
                id="preserveNucleic"
                checked={uiOptions.preserveNucleic}
                onChange={(e) => setUiOptions(o => ({ ...o, preserveNucleic: e.target.checked }))}
              />
              <label htmlFor="preserveNucleic" style={{ marginLeft: 8 }}>
                Preserve DNA or RNA chains <AiOutlineInfoCircle style={{ cursor: 'help' }} className="textlink" title="Remove DNA and RNA molecules present in the PDB file."/>
              </label>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
              <input
                type="checkbox"
                id="preserveCofactors"
                checked={uiOptions.preserveCofactors}
                onChange={(e) => setUiOptions(o => ({ ...o, preserveCofactors: e.target.checked }))}
              />
              <label htmlFor="preserveCofactors" style={{ marginLeft: 8 }}>
                Preserve cofactors <AiOutlineInfoCircle style={{ cursor: 'help' }} className="textlink" title="Remove cofactors (e.g., IhP, HEM, COH, NAD, NAP, ATP, GTP, ADP, GDP, UDP) present in the PDB file."/>
              </label>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', width: '100%', marginBottom: 5 }}>
              <input
                type="checkbox"
                id="oligodimerize"
                checked={uiOptions.oligodimerize}
                onChange={(e) => setUiOptions(o => ({ ...o, oligodimerize: e.target.checked }))}
              />
              <label htmlFor="oligodimerize" style={{ marginLeft: 8 }}>
                Oligodimerize the protein <AiOutlineInfoCircle style={{ cursor: 'help' }} className="textlink" title="Reduce the protein structure to a monomer to accelerate subsequent computations."/>
              </label>
            </div>

            <div className="spaceGraph"></div>

            <button className="sign-button button" type="submit" disabled={isSubmitDisabled}>
              {loading ? 'Processing...' : 'Submit'}
            </button>
          </form>

          <div className="riga">
            <Link className="textlink textLinkSign" to="/dashboard">
              Back to Dashboard <FaArrowRight size={12} style={{ marginLeft: 8, marginBottom: -1 }} />
            </Link>
          </div>
        </div>
        <div className="space"></div>
        </center>

        {/* Results Modal */}
        {showModal && (
          <div className="popup-overlay">
            <div className="popup-content">
              <div className="divCloseButton"><button onClick={() => setShowModal(false)} className="CloseButton text">X</button></div>
              <h4 className="popupTitle titolo">Results:</h4>
              <p className="resultText">
                {mode === 'single'
                  ? "The resulting PDB file has been saved on the CleanPDB Storage page."
                  : "The resulting PDB files have been saved on the CleanPDB Storage page."}
              </p>

              {mode === 'single' && (
                <ol>
                  {results.map((result, index) => (
                    <label key={index} className="resultText">
                      <li>
                        <a href={result.url} download={result.name} target="_blank" rel="noopener noreferrer">
                          {result.name}
                        </a>
                      </li>
                    </label>
                  ))}
                </ol>
              )}

              {location.state && location.state.nextPageIndex !== undefined ? (
                <button className="sign-button button" onClick={proceedPipeline}>Proceed to Next Pipeline Tool</button>
              ) : (
                <button className="sign-button button" onClick={() => navigate('/CleanPDBStorage')}>Go to CleanPDB Storage</button>
              )}
              <div className="riga">
                <Link
                  className="textlink textLinkSign"
                  to="/dashboard"
                >
                  Back to Dashboard{' '}
                  <FaArrowRight
                    size={12}
                    style={{ marginLeft: '8px', marginBottom: '-1px' }}
                  />
                </Link>
              </div>
              
            </div>
          </div>
        )}
    </>
  );
};

export default CleanPdbComponent;
